// CSC-295 — Web Push helpers shared by DesktopPushToggle (the notification popover
// footer, N7), the onboarding banner, and the Notifications card in ProfilePage.
//
// Single responsibility: bridge between the browser's PushManager / Notification
// APIs and the backend's /api/push endpoints. Stays UI-agnostic — the consuming
// components own all toast / banner copy.

import { apiClient } from '@features/shared/api/apiClient';
import type { ApiResponse } from '@features/shared/types';

export type PushStatus =
  | 'unsupported' // Browser doesn't expose Notification + ServiceWorker + PushManager.
  | 'denied'      // User explicitly blocked notifications. Cannot prompt again.
  | 'off'         // Permission is "default" or granted but no active subscription.
  | 'on';         // Permission granted AND a subscription is active server-side.

export function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

// Resolves the VAPID public key. Prefers the API endpoint (single source of truth
// on the backend) and falls back to the bundled VITE env when the API is offline.
async function resolveVapidPublicKey(): Promise<string> {
  try {
    const { data } = await apiClient.get<ApiResponse<{ publicKey: string }>>('/push/public-key');
    if (data.success && data.data?.publicKey) return data.data.publicKey;
  } catch {
    // Fall through to env fallback.
  }
  const fallback = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;
  if (fallback) return fallback;
  throw new Error('VAPID public key not configured');
}

// Converts a URL-safe base64 string (VAPID public key) into the Uint8Array shape
// PushManager.subscribe() expects.
function urlBase64ToUint8Array(base64UrlString: string): Uint8Array {
  const padding = '='.repeat((4 - (base64UrlString.length % 4)) % 4);
  const base64 = (base64UrlString + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function getRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  // ready resolves when the registered SW is active. Falls back to getRegistration()
  // when the SW is still installing on first visit.
  try {
    return await navigator.serviceWorker.ready;
  } catch {
    return navigator.serviceWorker.getRegistration().then((r) => r ?? null);
  }
}

// Returns 'on' only when permission is granted AND the browser holds an active
// PushSubscription. Browsers occasionally drop subscriptions silently (cleared
// site data, profile reset), so checking permission alone isn't enough.
export async function getPushStatus(): Promise<PushStatus> {
  if (!isPushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';

  const reg = await getRegistration();
  if (!reg) return 'off';

  const sub = await reg.pushManager.getSubscription();
  if (sub && Notification.permission === 'granted') return 'on';
  return 'off';
}

export async function registerPush(): Promise<PushStatus> {
  if (!isPushSupported()) return 'unsupported';

  // requestPermission() is a no-op when already granted; safe to always call.
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    return permission === 'denied' ? 'denied' : 'off';
  }

  const reg = await getRegistration();
  if (!reg) return 'off';

  const publicKey = await resolveVapidPublicKey();

  // PushManager.subscribe() returns the existing subscription when one exists,
  // so we can call it unconditionally — no need to manually getSubscription first.
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey),
  });

  // The browser-native subscription serializes to a SubscriptionJSON object with
  // `endpoint` + `keys.p256dh` + `keys.auth`. Reshape into the API DTO.
  const json = sub.toJSON();
  await apiClient.post('/push/subscribe', {
    endpoint: json.endpoint,
    p256dhKey: json.keys?.p256dh ?? '',
    authKey: json.keys?.auth ?? '',
    userAgent: navigator.userAgent.slice(0, 512),
  });

  return 'on';
}

export async function unsubscribePush(): Promise<PushStatus> {
  if (!isPushSupported()) return 'unsupported';

  const reg = await getRegistration();
  if (!reg) return 'off';

  const sub = await reg.pushManager.getSubscription();
  if (!sub) return 'off';

  // Notify the server first so the row is deactivated even if the local
  // unsubscribe fails. Fire-and-forget on the server side: returns 200 even
  // when the row doesn't exist (idempotent).
  try {
    await apiClient.post('/push/unsubscribe', { endpoint: sub.endpoint });
  } catch {
    // Network blip — the local unsubscribe still helps; the server-side row
    // gets cleaned up on the next 410 Gone the broker returns.
  }
  await sub.unsubscribe();
  return 'off';
}
